import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import WatchStar from "@/components/site/WatchStar";
import OpportunityPanel from "@/components/site/OpportunityPanel";

export default function TermPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/trends/${id}`).then((r) => setData(r.data)).catch((e) => setErr(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; referenced values are stable module imports/constants
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <Ticker />
        <div className="mx-auto max-w-[1400px] px-6 py-20 text-center">
          <h1 className="font-display font-extrabold text-5xl tracking-tighter">Term not found</h1>
          <Link to="/" className="overline mt-6 inline-block border border-black px-5 py-2 hover:bg-black hover:text-white">← Back to Atlas</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) return null;
  const t = data.trend;
  const countryName = t.country === "GLOBAL" ? "the world" : t.country;
  const title = `${t.term} — searched ${fmt(t.volume)} times in ${countryName} | Universtrend`;
  const desc = `Why ${t.term} is one of the most-searched terms in ${countryName}: live volume, category breakdown, and related trends. Monthly volume ${fmt(t.volume)}, ${t.delta >= 0 ? "up" : "down"} ${Math.abs(t.delta)}% week-over-week.`;

  return (
    <div data-testid="term-page" className="bg-white text-[#0a0a0a]">
      <Seo title={title} description={desc} />
      <Header />
      <Ticker />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-[#52525b] mb-4">
              {t.country === "GLOBAL" ? "Global" : t.country} · {t.category}
            </div>
            <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-8xl">
              {t.term}
            </h1>
            <div className="mt-6">
              <WatchStar trend={t} />
            </div>
            <p className="mt-6 max-w-2xl text-base lg:text-lg text-[#52525b]">
              Across {t.country === "GLOBAL" ? "the planet" : t.country}, people search for <b>{t.term}</b> roughly <b>{fmt(t.volume)}</b> times a month — placing it firmly inside the {t.category.toLowerCase()} obsessions of the season.
            </p>
            <div className="grid grid-cols-3 mt-10 border border-black">
              <div className="p-5 border-r border-black">
                <div className="overline text-[#52525b]">Monthly volume</div>
                <div className="font-display font-extrabold text-3xl lg:text-4xl mt-1">{fmt(t.volume)}</div>
              </div>
              <div className="p-5 border-r border-black">
                <div className="overline text-[#52525b]">Δ Week-over-week</div>
                <div className={`font-display font-extrabold text-3xl lg:text-4xl mt-1 ${t.delta >= 0 ? "text-[#ff3b30]" : ""}`}>
                  {t.delta >= 0 ? "▲" : "▼"} {Math.abs(t.delta)}%
                </div>
              </div>
              <div className="p-5">
                <div className="overline text-[#52525b]">Rank in {t.country === "GLOBAL" ? "Global" : t.country}</div>
                <div className="font-display font-extrabold text-3xl lg:text-4xl mt-1">#{t.rank}</div>
              </div>
            </div>
          </div>
          <aside className="col-span-12 lg:col-span-4 border border-black p-6">
            <div className="overline text-[#52525b] mb-3">Related in {t.category}</div>
            <ol className="divide-y divide-black/15">
              {data.related.map((r) => (
                <li key={r.id} className="py-3">
                  <Link to={`/trend/${r.id}`} className="flex justify-between hover:text-[#ff3b30]" data-testid={`related-${r.id}`}>
                    <span className="font-display font-semibold tracking-tight">{r.term}</span>
                    <span className="font-mono text-xs text-[#52525b]">{fmt(r.volume)}</span>
                  </Link>
                </li>
              ))}
            </ol>
            <Link to={`/category/${t.category.toLowerCase()}`} className="block mt-5 overline border border-black px-4 py-2 text-center hover:bg-black hover:text-white">
              See all in {t.category} →
            </Link>
          </aside>
        </div>
      </section>

      <OpportunityPanel trendId={t.id} term={t.term} />

      <Footer />
    </div>
  );
}
